<script setup>
import { ref, computed, onMounted } from 'vue';
import segueDashboardAPI from 'dashboard/api/segueDashboard';
import ConversaModal from './components/ConversaModal.vue';

// v91: Ofertas do dia no desenho do dashboard-leads:
//   - 1 linha por CPF POR BANCO (melhor oferta do cliente em cada banco)
//   - cards: Total / Clientes novos / FACTA / UY3 / C6 (bancos filtram)
//   - abas OFERTAS GERAL e OFERTAS VENDEDOR (agrupamento por dono)
const isLoading = ref(false);
const error = ref(null);
const data = ref(null);
const dia = ref('');
const bankFilter = ref('all');
const search = ref('');
const expanded = ref(null); // `${cpf}|${banco}` expandido
const aba = ref('geral'); // geral | vendedor
const vendedorFilter = ref(null); // filtro vindo do "visualizar" da aba vendedor
// v93: conversa estilo iPhone por telefone
const conversaPhone = ref(null);
const conversaNome = ref('');
const abrirWhats = c => {
  conversaPhone.value = c.telefone || '';
  conversaNome.value = '';
};

const load = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const { data: resp } = await segueDashboardAPI.fetchOfertas(
      dia.value || null
    );
    if (resp.error) error.value = resp.error;
    else data.value = resp;
  } catch (e) {
    error.value = e.message || 'Falha ao carregar ofertas';
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);

const clientes = computed(() => {
  let list = data.value?.clientes || [];
  if (bankFilter.value !== 'all')
    list = list.filter(c => c.banco === bankFilter.value);
  if (vendedorFilter.value !== null)
    list = list.filter(c => c.vendedor_id === vendedorFilter.value);
  const q = search.value.replace(/\D+/g, '');
  if (q.length >= 4)
    list = list.filter(
      c =>
        (c.cpf || '').includes(q) ||
        (c.telefone || '').replace(/\D+/g, '').includes(q)
    );
  return list;
});

// agrupamento POR VENDEDOR (client-side, mesma fonte da tabela geral)
const vendedores = computed(() => {
  const list = data.value?.clientes || [];
  const map = new Map();
  list.forEach(c => {
    if (!map.has(c.vendedor_id))
      map.set(c.vendedor_id, {
        id: c.vendedor_id,
        nome: c.vendedor,
        cpfs: new Set(),
        uy3: 0,
        facta: 0,
        c6: 0,
        total: 0,
      });
    const v = map.get(c.vendedor_id);
    v.cpfs.add(c.cpf);
    const val = c.valor_liquido || 0;
    if (c.banco === 'UY3') v.uy3 += val;
    else if (c.banco === 'FACTA') v.facta += val;
    else if (c.banco === 'C6') v.c6 += val;
    v.total += val;
  });
  return [...map.values()]
    .map(v => ({ ...v, clientes: v.cpfs.size }))
    .sort((a, b) => (a.id === 1 ? 1 : b.id === 1 ? -1 : b.total - a.total));
});

const brl = v =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const toggleBank = key => {
  bankFilter.value = bankFilter.value === key ? 'all' : key;
};

const verVendedor = v => {
  vendedorFilter.value = v.id;
  aba.value = 'geral';
};

const limparVendedor = () => {
  vendedorFilter.value = null;
};

const toggleExpand = c => {
  const key = `${c.cpf}|${c.banco}`;
  expanded.value = expanded.value === key ? null : key;
};

const vendedorFilterNome = computed(() => {
  if (vendedorFilter.value === null) return null;
  const v = vendedores.value.find(x => x.id === vendedorFilter.value);
  return v?.nome || `Agente ${vendedorFilter.value}`;
});

const fmtDia = computed(() => {
  const d = data.value?.dia;
  if (!d) return '';
  const [y, m, dd] = d.split('-');
  return `${dd}/${m}/${y}`;
});

const bankChip = b => {
  if (b === 'FACTA') return 'bg-n-amber-3 text-n-amber-11';
  if (b === 'UY3') return 'bg-n-blue-3 text-n-blue-11';
  return 'bg-n-teal-3 text-n-teal-11'; // C6
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-4 w-full h-full bg-n-background">
    <div class="flex flex-col gap-5 w-full">
      <!-- Header -->
      <div class="flex flex-wrap gap-4 justify-between items-start">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            Ofertas do dia · {{ fmtDia }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Melhor oferta por cliente em cada banco.
          </p>
        </div>
        <div class="flex flex-wrap gap-3 items-center">
          <input
            v-model="dia"
            type="date"
            class="px-3 h-9 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            @change="load"
          />
          <button
            type="button"
            class="flex gap-1.5 items-center px-4 h-9 text-sm font-semibold text-white rounded-lg shadow-sm bg-n-brand hover:opacity-90"
            @click="load"
          >
            <span class="i-lucide-refresh-cw size-4" />
            Atualizar
          </button>
        </div>
      </div>

      <p v-if="error" class="text-sm text-n-ruby-11">{{ error }}</p>

      <!-- Cards -->
      <div v-if="data" class="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div class="p-4 rounded-xl border-l-4 border border-n-weak border-l-n-amber-9 bg-n-solid-1">
          <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
            Total do dia
          </span>
          <p class="mt-1 text-xl font-bold tabular-nums text-n-slate-12">
            {{ brl(data.cards?.total?.valor) }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ data.cards?.total?.clientes || 0 }} clientes
          </p>
        </div>
        <div class="p-4 rounded-xl border-l-4 border border-n-weak border-l-n-teal-9 bg-n-solid-1">
          <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
            ✨ Clientes novos do dia
          </span>
          <p class="mt-1 text-xl font-bold tabular-nums text-n-teal-11">
            {{ brl(data.cards?.novos?.valor) }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ data.cards?.novos?.clientes || 0 }} novos ·
            <b class="text-n-teal-11">{{ data.cards?.novos?.pct || 0 }}% do total</b>
          </p>
        </div>
        <button
          type="button"
          class="p-4 text-left rounded-xl border"
          :class="
            bankFilter === 'FACTA'
              ? 'border-n-amber-9 bg-n-amber-2'
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-2'
          "
          @click="toggleBank('FACTA')"
        >
          <span class="text-[11px] font-medium uppercase tracking-wide text-n-amber-11">
            FACTA
          </span>
          <p class="mt-1 text-xl font-bold tabular-nums text-n-slate-12">
            {{ data.cards?.facta?.clientes || 0 }}
          </p>
          <p class="text-xs text-n-slate-11">{{ brl(data.cards?.facta?.valor) }}</p>
        </button>
        <button
          type="button"
          class="p-4 text-left rounded-xl border"
          :class="
            bankFilter === 'UY3'
              ? 'border-n-blue-9 bg-n-blue-2'
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-2'
          "
          @click="toggleBank('UY3')"
        >
          <span class="text-[11px] font-medium uppercase tracking-wide text-n-blue-11">
            UY3
          </span>
          <p class="mt-1 text-xl font-bold tabular-nums text-n-slate-12">
            {{ data.cards?.uy3?.clientes || 0 }}
          </p>
          <p class="text-xs text-n-slate-11">{{ brl(data.cards?.uy3?.valor) }}</p>
        </button>
        <button
          type="button"
          class="p-4 text-left rounded-xl border"
          :class="
            bankFilter === 'C6'
              ? 'border-n-teal-9 bg-n-teal-2'
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-2'
          "
          @click="toggleBank('C6')"
        >
          <span class="text-[11px] font-medium uppercase tracking-wide text-n-teal-11">
            C6
          </span>
          <p class="mt-1 text-xl font-bold tabular-nums text-n-slate-12">
            {{ data.cards?.c6?.clientes || 0 }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ (data.cards?.c6?.clientes || 0) > 0 ? brl(data.cards?.c6?.valor) : 'em breve' }}
          </p>
        </button>
      </div>

      <!-- Abas + busca -->
      <div class="flex flex-wrap gap-3 items-center">
        <div class="flex overflow-hidden rounded-lg border border-n-weak">
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-semibold"
            :class="
              aba === 'geral'
                ? 'bg-n-brand text-white'
                : 'bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="aba = 'geral'"
          >
            Ofertas Geral
          </button>
          <button
            type="button"
            class="px-4 py-1.5 text-sm font-semibold"
            :class="
              aba === 'vendedor'
                ? 'bg-n-brand text-white'
                : 'bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="aba = 'vendedor'"
          >
            Ofertas Vendedor
          </button>
        </div>
        <template v-if="aba === 'geral'">
          <input
            v-model="search"
            type="text"
            placeholder="Buscar por CPF ou telefone…"
            class="px-3 h-9 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 min-w-64"
          />
          <span
            v-if="vendedorFilterNome"
            class="inline-flex gap-2 items-center px-3 py-1 text-xs font-semibold rounded-full bg-n-brand/10 text-n-brand"
          >
            Vendedor: {{ vendedorFilterNome }}
            <button type="button" class="hover:opacity-70" @click="limparVendedor">
              ✕
            </button>
          </span>
          <span class="text-sm text-n-slate-11">
            {{ clientes.length }} linha(s)
          </span>
        </template>
      </div>

      <!-- ============ ABA GERAL ============ -->
      <div v-if="aba === 'geral'" class="overflow-x-auto rounded-xl border border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left bg-n-solid-2 text-n-slate-11">
              <th class="px-3 py-2.5 font-medium text-right">Qtd</th>
              <th class="px-3 py-2.5 font-medium">Banco</th>
              <th class="px-3 py-2.5 font-medium">Telefone</th>
              <th class="px-3 py-2.5 font-medium">CPF</th>
              <th class="px-3 py-2.5 font-medium text-right">Valor líquido</th>
              <th class="px-3 py-2.5 font-medium text-right">Prazo</th>
              <th class="px-3 py-2.5 font-medium text-right">Parcela</th>
              <th class="px-3 py-2.5 font-medium">Vendedor</th>
              <th class="px-3 py-2.5 font-medium">Hora</th>
              <th class="px-3 py-2.5 font-medium" />
            </tr>
          </thead>
          <tbody v-if="isLoading">
            <tr>
              <td colspan="10" class="px-3 py-6 text-center text-n-slate-11">
                Carregando…
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <template v-for="c in clientes" :key="c.cpf + c.banco">
              <tr
                class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
                @click="toggleExpand(c)"
              >
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-11">
                  {{ c.qtd_ofertas }}
                </td>
                <td class="px-3 py-2.5 whitespace-nowrap">
                  <span
                    class="px-2 py-0.5 text-xs font-semibold rounded"
                    :class="bankChip(c.banco)"
                  >
                    {{ c.banco }}
                  </span>
                  <span
                    v-if="c.novo"
                    class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-n-teal-3 text-n-teal-11"
                    title="Cliente novo do dia"
                  >
                    NOVO
                  </span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-12">{{ c.telefone }}</td>
                <td class="px-3 py-2.5 tabular-nums text-n-slate-12">{{ c.cpf }}</td>
                <td class="px-3 py-2.5 font-semibold text-right tabular-nums text-n-slate-12">
                  {{ brl(c.valor_liquido) }}
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                  {{ c.prazo ? c.prazo + 'x' : '—' }}
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                  {{ c.parcela ? brl(c.parcela) : '—' }}
                </td>
                <td class="px-3 py-2.5 text-n-slate-12">{{ c.vendedor }}</td>
                <td class="px-3 py-2.5 text-n-slate-11">{{ c.hora }}</td>
                <td class="px-3 py-2.5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    class="inline-flex justify-center items-center mr-1 text-white rounded-lg align-middle size-7 bg-n-teal-9 hover:opacity-90"
                    title="Ver conversa"
                    @click.stop="abrirWhats(c)"
                  >
                    <span class="i-lucide-message-circle size-4" />
                  </button>
                  <span
                    class="inline-block size-3.5 text-n-slate-11 align-middle"
                    :class="
                      expanded === `${c.cpf}|${c.banco}`
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                  />
                </td>
              </tr>
              <tr v-if="expanded === `${c.cpf}|${c.banco}`" class="bg-n-solid-1">
                <td colspan="10" class="px-6 py-3">
                  <table class="w-full text-xs">
                    <thead>
                      <tr class="text-left text-n-slate-11">
                        <th class="py-1 pr-4 font-medium text-right">Valor</th>
                        <th class="py-1 pr-4 font-medium text-right">Prazo</th>
                        <th class="py-1 pr-4 font-medium text-right">Parcela</th>
                        <th class="py-1 pr-4 font-medium">Tabela</th>
                        <th class="py-1 font-medium">Hora</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(o, i) in c.ofertas"
                        :key="i"
                        class="border-t border-n-weak/50"
                      >
                        <td class="py-1.5 pr-4 text-right tabular-nums text-n-slate-12">
                          {{ brl(o.valor_liquido) }}
                        </td>
                        <td class="py-1.5 pr-4 text-right tabular-nums text-n-slate-12">
                          {{ o.prazo ? o.prazo + 'x' : '—' }}
                        </td>
                        <td class="py-1.5 pr-4 text-right tabular-nums text-n-slate-12">
                          {{ o.parcela ? brl(o.parcela) : '—' }}
                        </td>
                        <td
                          class="py-1.5 pr-4 max-w-64 truncate text-n-slate-11"
                          :title="o.tabela"
                        >
                          {{ o.tabela || '—' }}
                        </td>
                        <td class="py-1.5 text-n-slate-11">{{ o.hora }}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
            <tr v-if="!clientes.length">
              <td colspan="10" class="px-3 py-6 text-center text-n-slate-11">
                Sem ofertas no dia
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ ABA VENDEDOR ============ -->
      <div v-else class="overflow-x-auto rounded-xl border border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left bg-n-solid-2 text-n-slate-11">
              <th class="px-3 py-2.5 font-medium">Vendedor</th>
              <th class="px-3 py-2.5 font-medium text-right">Qtd. Clientes</th>
              <th class="px-3 py-2.5 font-medium text-right">Ofertas UY3</th>
              <th class="px-3 py-2.5 font-medium text-right">Ofertas FACTA</th>
              <th class="px-3 py-2.5 font-medium text-right">Ofertas C6</th>
              <th class="px-3 py-2.5 font-medium text-right">
                Valor total de ofertas
              </th>
              <th class="px-3 py-2.5 font-medium" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="v in vendedores"
              :key="v.id"
              class="border-t border-n-weak hover:bg-n-alpha-1"
            >
              <td class="px-3 py-2.5 font-medium text-n-slate-12">{{ v.nome }}</td>
              <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                {{ v.clientes }}
              </td>
              <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                {{ v.uy3 ? brl(v.uy3) : '—' }}
              </td>
              <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                {{ v.facta ? brl(v.facta) : '—' }}
              </td>
              <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-12">
                {{ v.c6 ? brl(v.c6) : '—' }}
              </td>
              <td class="px-3 py-2.5 font-semibold text-right tabular-nums text-n-slate-12">
                {{ brl(v.total) }}
              </td>
              <td class="px-3 py-2.5 text-right">
                <button
                  type="button"
                  class="px-3 py-1 text-xs font-semibold rounded-lg border border-n-amber-9 text-n-amber-11 hover:bg-n-amber-2"
                  @click="verVendedor(v)"
                >
                  Visualizar ofertas
                </button>
              </td>
            </tr>
            <tr v-if="!vendedores.length">
              <td colspan="7" class="px-3 py-6 text-center text-n-slate-11">
                Sem ofertas no dia
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- v93: conversa estilo iPhone -->
    <ConversaModal
      v-if="conversaPhone"
      :phone="conversaPhone"
      :nome="conversaNome"
      @close="conversaPhone = null"
      @navigate="conversaPhone = null"
    />
  </div>
</template>
